import React, { useState } from 'react';
import {
	IAccommodationService,
	ICodeName,
	IFeeService,
	IFlightAncillaryService,
	IFlightService,
	IPassenger,
	IServices,
	ITransferService,
	TypeServiceEnum,
} from 'features/salesOrder/types';

import { Backdrop, Box, CircularProgress, Alert, Divider } from '@mui/material';
import { ServiceCorporateCardSection } from 'features/corporateCards/components/ServiceCorporateCardSection';
import { FlightService } from './FlightService';
import { IValidatingCarrier } from 'features/transactions/types';
import { AccommodationService } from './AccomodationService';
import { TransferService } from './TransferService';
import { OtherService } from './OtherService';
import { ShowAlertState } from 'features/common/types';
import { useStyles } from 'features/salesOrder/components/SalesOrderPassagerForm/styles';
import { FeeService } from './FeeService';
import { FlightAncillaryService } from './FlightAncillaryService';

interface Props {
	service?: IServices;
	serviceIndex?: number;
	saleOrderId?: string;
	passengers: IPassenger[];
	validatingCarriers: IValidatingCarrier[];
	currencyesList: ICodeName[];
	isExistingService?: boolean;
	onUpdateService: () => void;
	onSetAlert: (values: ShowAlertState) => void;
	isEnabled?: boolean;
}

export const ServiceForm = ({
	service,
	passengers,
	serviceIndex,
	saleOrderId,
	validatingCarriers,
	currencyesList,
	onUpdateService,
	onSetAlert,
	isExistingService,
	isEnabled = true,
}: Props): JSX.Element => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const isServiceActive = isEnabled;

	const applicablePaxes = passengers.filter((p) => p.isPhysicalPerson === true);
	return (
		<Box
			display="flex"
			flexDirection="column"
			gap={2}
			p={2}
			sx={{
				borderRadius: '4px',
			}}
		>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>

			{isExistingService && !isServiceActive && (
				<Alert severity="error" variant="outlined">
					Servicio inactivo. El formulario es solo lectura. Activá el servicio para editar y guardar cambios.
				</Alert>
			)}

			<Box
				component="fieldset"
				disabled={!isServiceActive}
				sx={{
					border: 'none',
					margin: 0,
					padding: 0,
					minWidth: 0,
					...(!isServiceActive && {
						color: 'text.disabled',
						'& .MuiTypography-root': {
							color: 'text.disabled',
						},
						'& .MuiInputBase-root': {
							color: 'text.disabled',
						},
						'& .MuiInputBase-input, & .MuiSelect-select': {
							color: 'text.disabled',
							WebkitTextFillColor: (theme) => theme.palette.text.disabled,
						},
						'& .MuiFormLabel-root, & .MuiInputLabel-root, & .MuiFormControlLabel-label': {
							color: 'text.disabled',
						},
						'& .MuiChip-root': {
							color: 'text.disabled',
							borderColor: 'action.disabled',
						},
						'& .MuiFormHelperText-root': {
							color: 'text.disabled',
						},
						'& .MuiSvgIcon-root': {
							color: 'action.disabled',
						},
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: 'action.disabledBackground',
						},
						'& .MuiButton-root.Mui-disabled': {
							backgroundColor: 'action.disabledBackground',
							color: 'action.disabled',
							borderColor: 'action.disabledBackground',
							boxShadow: 'none',
						},
						'& .MuiButton-outlined.Mui-disabled': {
							backgroundColor: 'transparent',
						},
					}),
				}}
			>
				{service?.serviceType === TypeServiceEnum.Flight && serviceIndex !== undefined && (
					<FlightService
						index={serviceIndex}
						passengers={applicablePaxes}
						// @ts-ignore
						service={service as IFlightService}
						currencyesList={currencyesList}
						validatingCarriers={validatingCarriers}
						onUpdateService={onUpdateService}
						onSetAlert={onSetAlert}
						onSetLoading={setLoading}
						isExistingService={isExistingService}
					/>
				)}
				{service?.serviceType === TypeServiceEnum.FlightAncillary && serviceIndex !== undefined && (
					<FlightAncillaryService
						index={serviceIndex}
						passengers={applicablePaxes}
						// @ts-ignore
						service={service as IFlightAncillaryService}
						currencyesList={currencyesList}
						validatingCarriers={validatingCarriers}
						onUpdateService={onUpdateService}
						onSetAlert={onSetAlert}
						onSetLoading={setLoading}
						isExistingService={isExistingService}
					/>
				)}
				{service?.serviceType === TypeServiceEnum.Accommodation && serviceIndex !== undefined && (
					<AccommodationService
						index={serviceIndex}
						passengers={applicablePaxes}
						// @ts-ignore
						service={service as IAccommodationService}
						currencyesList={currencyesList}
						onUpdateService={onUpdateService}
						onSetAlert={onSetAlert}
						onSetLoading={setLoading}
						isExistingService={isExistingService}
					/>
				)}
				{service?.serviceType === TypeServiceEnum.Transfer && serviceIndex !== undefined && (
					<TransferService
						index={serviceIndex}
						passengers={applicablePaxes}
						// @ts-ignore
						service={service as ITransferService}
						currencyesList={currencyesList}
						onUpdateService={onUpdateService}
						onSetAlert={onSetAlert}
						onSetLoading={setLoading}
						isExistingService={isExistingService}
					/>
				)}
				{service?.serviceType === TypeServiceEnum.Other && serviceIndex !== undefined && (
					<OtherService
						index={serviceIndex}
						passengers={applicablePaxes}
						// @ts-ignore
						service={service as ITransferService}
						currencyesList={currencyesList}
						onUpdateService={onUpdateService}
						onSetAlert={onSetAlert}
						onSetLoading={setLoading}
						isExistingService={isExistingService}
					/>
				)}
				{service?.serviceType === TypeServiceEnum.Fee && serviceIndex !== undefined && (
					<FeeService
						index={serviceIndex}
						passengers={applicablePaxes}
						// @ts-ignore
						service={service as IFeeService}
						currencyesList={currencyesList}
						onUpdateService={onUpdateService}
						onSetAlert={onSetAlert}
						onSetLoading={setLoading}
						isExistingService={isExistingService}
					/>
				)}
			</Box>

			{isExistingService && service?.keyId && saleOrderId && (
				<>
					<Divider />
					<ServiceCorporateCardSection
						serviceId={service.keyId}
						saleOrderId={saleOrderId}
						serviceCostUsd={parseFloat(
							String(
								(service as unknown as Record<string, unknown>).totalCost ??
									(service as unknown as Record<string, unknown>).cost ??
									0,
							),
						)}
						providerName={
							((service as unknown as Record<string, unknown>).serviceProvider as Record<string, string> | undefined)
								?.fantasyName ?? undefined
						}
					/>
				</>
			)}
		</Box>
	);
};
